import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { GalleryHorizontal, LayoutGrid } from 'lucide-react';
import { Button, PageLoader, SegmentedToggle, Tabs, Typography } from '../ui';
import { PageHeader } from '../PageHeader';
import { ConnectorRow } from '../home2/Home2ConnectorsSection';
import { RoutinesBody } from '../routines/RoutinesBody';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useNarrow } from '../../../lib/use-narrow';
import { useWins } from '../../../lib/use-wins';
import { useRoutines } from '../../../lib/routines';
import { activeRecommendedSplit } from '../../../lib/routine-groups';
import { sortConnectors, useAppsController, type AppsController } from '../../../lib/use-connectors';
import { homeConnectorGroups } from '../../../lib/home-connector-groups';
import { useAlfredInOutreachEnabled } from '../../../lib/alfred-in-outreach-flag';
import type { ConnectorStatus } from '../../../lib/connectors';
import {
  winsPageShape,
  WINS_RECOMMENDED_CONNECTORS,
  type WinsConnectorsLine,
  type WinsConnectorsRead,
} from '../../../lib/wins-page-shape';
import { buildWinCaption, winsInPeriod, type WebWin, type WinPeriod } from '../../../lib/wins';
import { WinCard } from './WinCard';
import { RecapCard } from './RecapCard';
import { WinsReel, type ReelItem } from './WinsReel';
import { ShareWinDialog, type ShareRequest } from './ShareWinDialog';
import styles from './WinsBody.module.css';

type ViewMode = 'reel' | 'grid';
const PAGE = 'wins_page';
const ROW_PX = 4;
const GAP_PX = 20;

/** A grid cell that spans as many 4px rows as its card is tall, so the gallery packs like masonry. */
function MasonryCell({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      el.style.gridRowEnd = 'auto';
      el.style.gridRowEnd = `span ${Math.ceil((el.offsetHeight + GAP_PX) / ROW_PX)}`;
    };
    fit();
    const child = el.firstElementChild;
    if (typeof ResizeObserver === 'undefined' || !child) return;
    const ro = new ResizeObserver(fit);
    ro.observe(child);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} className={styles.cell}>
      {children}
    </div>
  );
}

/** The recommended connectors, each its own card, with the Connect the Home section uses. */
function RecommendedConnectors({
  connectors,
  controller,
  line,
  onConnect,
}: {
  connectors: ConnectorStatus[];
  controller: AppsController;
  line: WinsConnectorsLine;
  onConnect: (connector: ConnectorStatus) => void;
}) {
  const { t } = useTranslation();
  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <Typography variant="h4" color="primary">
          {t('wins.connectors.title')}
        </Typography>
        <Typography variant="p2" color="secondary">
          {t(line === 'first' ? 'wins.connectors.firstLine' : 'wins.connectors.moreLine')}
        </Typography>
      </div>
      <div className={styles.connectorList}>
        {connectors.map((c) => (
          <ConnectorRow key={c.toolkit} connector={c} controller={controller} variant="recommended" standalone onConnect={onConnect} />
        ))}
      </div>
    </section>
  );
}

/**
 * The Wins page: what the agents got done for the reader, newest first, as a reel (default) or a
 * masonry grid, with Weekly / Monthly tabs that put a recap first and narrow the gallery to the period.
 *
 * With no wins yet it points at what produces one (`winsPageShape`): the routines the connected apps
 * offer, and the connectors still to connect. The same recommendation follows the gallery when wins
 * exist but nothing is connected.
 *
 * Both phones draw the same screen (iOS `WinsScreen`, Android `WinsScreen`) off the `wins` native-data
 * action, which serves this page's `/api/wins` JSON.
 */
export function WinsBody() {
  const { t } = useTranslation();
  const productName = useProductName();
  const { logPageClickBi } = useGenericBi();
  const narrow = useNarrow();
  const data = useWins();
  const apps = useAppsController();
  const alfredOnOutreach = useAlfredInOutreachEnabled();
  const routinesRead = useRoutines();
  useLoadBi('wins-data', { isLoading: data.isLoading, isError: data.isError });
  useLoadBi('wins-connectors-data', { isLoading: apps.state.kind === 'loading', isError: apps.state.kind === 'error' });

  const appsState = apps.state;
  const groups = useMemo(
    () => (appsState.kind === 'ready' && appsState.enabled ? homeConnectorGroups(appsState.connectors, alfredOnOutreach) : null),
    [appsState, alfredOnOutreach],
  );
  const recommendedConnectors = useMemo(
    () => (groups ? sortConnectors(groups.recommended).slice(0, WINS_RECOMMENDED_CONNECTORS) : []),
    [groups],
  );
  // The routines the connected apps' kits installed. A kit only reaches an account that has its app, and
  // a disconnected app's routines are system-paused, which the split already leaves out.
  const kitRoutines = useMemo(
    () => (routinesRead.state.kind === 'ready' ? routinesRead.state.routines.filter((r) => !!r.sourceConnectorSlug) : []),
    [routinesRead.state],
  );
  const routineRows = useMemo(() => {
    const split = activeRecommendedSplit(kitRoutines);
    return split.active.length + split.recommended.length;
  }, [kitRoutines]);

  const connectorsRead: WinsConnectorsRead =
    appsState.kind === 'loading'
      ? 'loading'
      : groups
        ? { connected: groups.connected.length, recommended: recommendedConnectors.length }
        : 'unavailable';
  const shape = winsPageShape(
    data.isLoading ? 'loading' : data.isError ? 'error' : { count: data.wins.length },
    connectorsRead,
    // A failed routines read offers none rather than holding the page on a loader.
    routinesRead.state.kind === 'loading' ? 'loading' : { rows: routineRows },
  );

  const connectClicked = (connector: ConnectorStatus) =>
    logPageClickBi({ page_name: PAGE, button_name: 'connect_connector' }, { componentName: connector.toolkit });

  const [period, setPeriod] = useState<WinPeriod>('all');
  const [mode, setMode] = useState<ViewMode>('reel');
  const [share, setShare] = useState<ShareRequest | null>(null);
  const now = useMemo(() => new Date(), []);
  const wins = useMemo(() => winsInPeriod(data.wins, period, now), [data.wins, period, now]);

  const shareWin = (win: WebWin, from?: DOMRect) => {
    logPageClickBi({ page_name: PAGE, button_name: 'share_win' }, { componentName: win.id });
    setShare({
      title: t('wins.share.title'),
      caption: buildWinCaption(t('wins.share.winSentence', { title: win.title.replace(/[.!?。！？]+$/u, ''), productName })),
      preview: <WinCard win={win} layout="preview" />,
      entity: win.id,
      from,
    });
  };

  const recapPeriod = period === 'all' ? null : period;
  const shareRecap = () => {
    if (!recapPeriod) return;
    logPageClickBi({ page_name: PAGE, button_name: 'share_recap' }, { componentName: `recap-${recapPeriod}` });
    setShare({
      title: t('wins.share.recapTitle'),
      caption: buildWinCaption(
        t(recapPeriod === 'week' ? 'wins.share.recapWeekSentence' : 'wins.share.recapMonthSentence', { count: wins.length, productName }),
      ),
      preview: <RecapCard period={recapPeriod} wins={wins} now={now} layout="preview" />,
      entity: `recap-${recapPeriod}`,
    });
  };

  const pickPeriod = (p: WinPeriod) => {
    logPageClickBi({ page_name: PAGE, button_name: `tab_${p}` });
    setPeriod(p);
  };
  const pickMode = (m: ViewMode) => {
    logPageClickBi({ page_name: PAGE, button_name: `view_${m}` });
    setMode(m);
  };

  const reelItems: ReelItem[] = [
    ...(recapPeriod && wins.length > 0
      ? [{ key: `recap-${recapPeriod}`, uncounted: true, render: () => <RecapCard period={recapPeriod} wins={wins} now={now} layout="reel" onShare={shareRecap} /> }]
      : []),
    ...wins.map((win) => ({
      key: win.id,
      render: (focused: boolean) => <WinCard win={win} layout="reel" showShare={focused} onShare={(from) => shareWin(win, from)} />,
    })),
  ];

  let content: ReactNode;
  if (shape.kind === 'loading') {
    content = <PageLoader />;
  } else if (shape.kind === 'error') {
    content = (
      <div className={styles.state}>
        <Typography variant="p2" color="secondary">
          {t('wins.error')}
        </Typography>
        <Button variant="outlined" onClick={data.refetch}>
          {t('common.retry')}
        </Button>
      </div>
    );
  } else if (shape.kind === 'empty') {
    content =
      !shape.routines && !shape.connectors ? (
        <div className={styles.state}>
          <Typography variant="h3" color="primary">
            {t('wins.empty.title')}
          </Typography>
          <Typography variant="p2" color="secondary">
            {t('wins.empty.all')}
          </Typography>
        </div>
      ) : (
        <div className={styles.empty}>
          {shape.routines && (
            <section className={styles.section}>
              <div className={styles.sectionHead}>
                <Typography variant="h4" color="primary">
                  {t('wins.routines.title')}
                </Typography>
                <Typography variant="p2" color="secondary">
                  {t('wins.routines.line')}
                </Typography>
              </div>
              <RoutinesBody recommendedOnly />
            </section>
          )}
          {shape.connectors && (
            <RecommendedConnectors
              connectors={recommendedConnectors}
              controller={apps}
              line={shape.connectors}
              onConnect={connectClicked}
            />
          )}
        </div>
      );
  } else if (wins.length === 0) {
    content = (
      <div className={styles.state}>
        <Typography variant="h3" color="primary">
          {t('wins.empty.title')}
        </Typography>
        <Typography variant="p2" color="secondary">
          {t(period === 'week' ? 'wins.empty.week' : period === 'month' ? 'wins.empty.month' : 'wins.empty.all')}
        </Typography>
      </div>
    );
  } else if (mode === 'reel') {
    content = <WinsReel key={`${period}`} items={reelItems} paused={!!share} />;
  } else {
    content = (
      <>
        {recapPeriod && (
          <div className={styles.recap}>
            <RecapCard period={recapPeriod} wins={wins} now={now} layout="grid" onShare={shareRecap} />
          </div>
        )}
        <div className={styles.grid}>
          {wins.map((win) => (
            <MasonryCell key={win.id}>
              <WinCard win={win} layout="grid" onShare={(from) => shareWin(win, from)} />
            </MasonryCell>
          ))}
        </div>
        {!data.hasMore && (
          <Typography variant="p3" color="secondary" align="center" className={styles.end}>
            {t('wins.end')}
          </Typography>
        )}
      </>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader title={t('wins.title')} description={t('wins.lede')} />
      {/* Nothing to filter or switch until a win exists. */}
      {shape.kind === 'wins' && (
      <div className={styles.bar}>
        <Tabs<WinPeriod>
          label={t('wins.tabs.label')}
          size={narrow ? 'medium' : 'large'}
          value={period}
          onChange={pickPeriod}
          items={[
            { value: 'all', label: t('wins.tabs.all') },
            { value: 'week', label: t('wins.tabs.week') },
            { value: 'month', label: t('wins.tabs.month') },
          ]}
        />
        <SegmentedToggle<ViewMode>
          ariaLabel={t('wins.view.label')}
          type="iconOnly"
          className={styles.toggle}
          value={mode}
          onChange={pickMode}
          options={[
            { value: 'reel', label: t('wins.view.reel'), icon: <GalleryHorizontal size={16} aria-hidden /> },
            { value: 'grid', label: t('wins.view.grid'), icon: <LayoutGrid size={16} aria-hidden /> },
          ]}
        />
      </div>
      )}
      {content}
      {shape.kind === 'wins' && data.hasMore && (
        <div className={styles.more}>
          <Button variant="outlined" loading={data.loadingMore} onClick={() => {
            logPageClickBi({ page_name: PAGE, button_name: 'load_more' });
            data.loadMore();
          }}>
            {t('wins.loadMore')}
          </Button>
        </div>
      )}
      {shape.kind === 'wins' && shape.connectors && (
        <div className={styles.after}>
          <RecommendedConnectors connectors={recommendedConnectors} controller={apps} line="more" onConnect={connectClicked} />
        </div>
      )}
      <ShareWinDialog request={share} onClose={() => setShare(null)} />
    </div>
  );
}
